/**
 * Actycrea: marco.esm.js (ES6)
 * MIT
 *
 * Drives the interactive "Hero Centenario" configurator ( Iberia100 module
 * 4/12, `module-marco` ). Four independent behaviours over a single section:
 *
 *  1. Livery switch  — top-left buttons crossfade the full-bleed background.
 *  2. Size presets   — top-center buttons animate the central frame width.
 *  3. Logo switch    — top-right buttons crossfade the logo inside the frame.
 *  4. Manual resize  — the two side handles resize the frame symmetrically
 *                      from its center ( GSAP Draggable ), so the logo and
 *                      content stay centered. The helper hint fades out after
 *                      the first drag.
 *
 * The logo is always kept centered by CSS ( see _marco.scss ); this module
 * only toggles which variant is visible, never its position.
 *
 * Honors `prefers-reduced-motion`: swaps and resizes are applied instantly,
 * without tweens.
 */
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { PREFIX } from '../helpers/config.js';

gsap.registerPlugin( Draggable );

const SELECTOR_ROOT = `[data-${PREFIX}-marco]`;

const DEFAULTS = {
  // Crossfade duration ( seconds ) for background and logo swaps.
  fade: 0.5 ,
  // Preset resize duration ( seconds ).
  resize: 0.5
};

/**
 * Whether the user asked for reduced motion.
 *
 * @returns {boolean}
 */
function prefersReducedMotion( ){
  return !!( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches );
}

/**
 * Clamps a value between a minimum and a maximum.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp( value , min , max ){
  return Math.min( Math.max( value , min ) , max );
}

export class Marco {
  /**
   * @param {HTMLElement} element - The module section root.
   * @param {Object}      config  - Overrides for DEFAULTS ( fade, resize ).
   */
  constructor( element , config = { } ){
    if( !( element instanceof HTMLElement ) ) throw new Error( 'Marco: element inválido' );

    this._config  = { ...DEFAULTS , ...config };
    this._element = element;
    this._reduced = prefersReducedMotion( );

    this._frame  = element.querySelector( `[data-${PREFIX}-marco-frame]` );
    this._helper = element.querySelector( `[data-${PREFIX}-marco-helper]` );

    this._bgButtons   = Array.from( element.querySelectorAll( `[data-${PREFIX}-marco-bg]` ) );
    this._bgItems     = Array.from( element.querySelectorAll( `[data-${PREFIX}-marco-bg-item]` ) );
    this._bgLayers    = Array.from( element.querySelectorAll( `[data-${PREFIX}-marco-bg-layer]` ) );
    this._sizeButtons = Array.from( element.querySelectorAll( `[data-${PREFIX}-marco-size]` ) );
    this._logoButtons = Array.from( element.querySelectorAll( `[data-${PREFIX}-marco-logo]` ) );
    this._logoLayers  = Array.from( element.querySelectorAll( `[data-${PREFIX}-marco-logo-layer]` ) );
    this._handles     = Array.from( element.querySelectorAll( `[data-${PREFIX}-marco-handle]` ) );

    this._minWidth = parseInt( element.getAttribute( `data-${PREFIX}-marco-min` ) , 10 ) || 0;
    this._maxWidth = parseInt( element.getAttribute( `data-${PREFIX}-marco-max` ) , 10 ) || 0;

    // Autoplay: seconds each livery stays on screen ( 0 disables it ).
    const rawInterval = parseInt( element.getAttribute( `data-${PREFIX}-marco-interval` ) , 10 );
    this._interval = Number.isFinite( rawInterval ) ? rawInterval : 6;

    // Hover mode: the switch buttons also react on hover, not only on click.
    this._hover = element.hasAttribute( `data-${PREFIX}-marco-hover` );

    // Progress strokes ( one per livery item ), aligned with the bg buttons.
    this._libreaRings = this._bgItems.map( ( item ) => item.querySelector( `[data-${PREFIX}-marco-progress]` ) );
    this._currentBg = 0;
    this._autoTimer = null;

    this._curve = this._frame ? this._frame.querySelector( '.marco-frame-curve' ) : null;

    // Preset widths ( px ), read from the size buttons. They set the drag
    // range: the handles cannot go wider than the widest preset ( desktop )
    // nor narrower than the narrowest ( mobile ).
    this._presetWidths = this._sizeButtons
      .map( ( button ) => parseInt( button.getAttribute( `data-${PREFIX}-marco-size` ) , 10 ) )
      .filter( ( width ) => width > 0 );

    // Preset widths sorted: mobile ( min ), tablet ( median ), desktop ( max ).
    // They drive the frame size state ( which composes the logos ) and the
    // curve visibility ( hidden below tablet ).
    const sortedWidths = [ ...this._presetWidths ].sort( ( a , b ) => a - b );
    this._mobileWidth  = sortedWidths.length ? sortedWidths[ 0 ] : 0;
    this._tabletWidth  = sortedWidths.length ? sortedWidths[ Math.floor( sortedWidths.length / 2 ) ] : 0;
    this._desktopWidth = sortedWidths.length ? sortedWidths[ sortedWidths.length - 1 ] : 0;

    this._draggables = [ ];

    // Seed the crossfade layers with inline opacity so GSAP has a starting
    // value: otherwise the very first swap would jump ( CSS class sets the
    // final opacity instantly and the tween has nothing to animate ).
    this._initLayers( this._bgLayers );
    this._initLayers( this._logoLayers );

    this._bindLivery( );
    this._bindSizePresets( );
    this._bindLogo( );
    this._bindHandles( );

    // Initial curve visibility and logo composition state for the starting
    // ( tablet ) width.
    const initialWidth = this._frame ? this._frame.offsetWidth : 0;
    this._syncCurve( initialWidth );
    this._syncSizeState( initialWidth );

    // Auto-advancing livery carousel with a progress ring on the active thumb.
    this._initRings( );
    this._startAutoplay( );
  }

  /**
   * Seeds a crossfade layer stack with explicit inline opacity, matching the
   * initial `is-active` state, so the first tween animates from a known value.
   *
   * @param {HTMLElement[]} layers
   * @returns {void}
   */
  _initLayers( layers ){
    layers.forEach( ( layer ) => {
      gsap.set( layer , { opacity: layer.classList.contains( 'is-active' ) ? 1 : 0 } );
    } );
  }

  /**
   * Drag limits. The range is bounded by the presets ( widest = desktop,
   * narrowest = mobile ), capped at the stage width so the frame can't
   * overflow. Falls back to the ACF min/max ( then the stage ) when there are
   * no presets.
   *
   * @returns {{ min:number , max:number }}
   */
  _bounds( ){
    const stage = this._frame ? this._frame.parentElement : null;
    const stageWidth = stage ? stage.clientWidth : this._element.clientWidth;

    let max = this._presetWidths.length ? Math.max( ...this._presetWidths ) : ( this._maxWidth > 0 ? this._maxWidth : stageWidth );
    let min = this._presetWidths.length ? Math.min( ...this._presetWidths ) : ( this._minWidth > 0 ? this._minWidth : Math.min( 160 , max ) );

    // Never exceed the stage width, so the frame can't overflow horizontally.
    max = Math.min( max , stageWidth );
    return { min: Math.min( min , max ) , max };
  }

  /* ============
   * Livery ( background ) switch + autoplay
   * ===========*/
  _bindLivery( ){
    this._bgButtons.forEach( ( button ) => {
      this._bindActivation( button , ( ) => {
        const index = parseInt( button.getAttribute( `data-${PREFIX}-marco-bg` ) , 10 );
        // A manual pick restarts the autoplay timer from this livery.
        this._selectBg( index );
        this._hideHelper( );
      } );
    } );
  }

  /**
   * Binds an activation handler to a button: always on click, and also on
   * hover when hover mode is enabled ( ACF `hover_mode` ).
   *
   * @param {HTMLElement} button
   * @param {Function}    handler
   * @returns {void}
   */
  _bindActivation( button , handler ){
    button.addEventListener( 'click' , handler );
    if( this._hover ){
      button.addEventListener( 'mouseenter' , handler );
    }
  }

  /**
   * Activates a livery: swaps the background, reveals its label, resets the
   * progress rings and restarts the autoplay countdown from it.
   *
   * @param {number} index
   * @returns {void}
   */
  _selectBg( index ){
    const button = this._bgButtons[ index ];
    if( !button ) return;

    this._currentBg = index;
    this._activate( this._bgButtons , button );

    // Reveal the selected livery's label ( shown to the left of its thumb ).
    const item = button.closest( `[data-${PREFIX}-marco-bg-item]` );
    this._bgItems.forEach( ( node ) => node.classList.toggle( 'is-active' , node === item ) );

    this._crossfade( this._bgLayers , index );
    this._resetRings( );
    this._scheduleNext( );
  }

  /**
   * Initialises the progress strokes. `pathLength="100"` normalises every
   * perimeter to 100 units, so the dash works for both the circle and the pill
   * regardless of size. Starts them empty and matches each item's geometry.
   *
   * @returns {void}
   */
  _initRings( ){
    this._libreaRings.forEach( ( ring , index ) => {
      if( !ring ) return;
      gsap.set( ring , { strokeDasharray: 100 , strokeDashoffset: 100 } );
      this._updateRingGeometry( this._bgItems[ index ] , ring );
    } );
  }

  /**
   * Empties every progress stroke ( offset = full length ).
   *
   * @returns {void}
   */
  _resetRings( ){
    this._libreaRings.forEach( ( ring ) => {
      if( ring ) gsap.set( ring , { strokeDashoffset: 100 } );
    } );
  }

  /**
   * Keeps a progress rect matching its item's pixel box, so the stroke traces
   * the whole pill ( image + label ) — or a circle when there is no label.
   *
   * @param {HTMLElement} item
   * @param {SVGRectElement} rect
   * @returns {void}
   */
  _updateRingGeometry( item , rect ){
    if( !item || !rect ) return;
    const w = item.offsetWidth;
    const h = item.offsetHeight;
    if( rect._w === w && rect._h === h ) return;
    rect._w = w;
    rect._h = h;

    const svg = rect.ownerSVGElement;
    if( svg ) svg.setAttribute( 'viewBox' , `0 0 ${w} ${h}` );
    rect.setAttribute( 'x' , 1 );
    rect.setAttribute( 'y' , 1 );
    rect.setAttribute( 'width' , Math.max( 0 , w - 2 ) );
    rect.setAttribute( 'height' , Math.max( 0 , h - 2 ) );
    rect.setAttribute( 'rx' , Math.max( 0 , ( h - 2 ) / 2 ) );
  }

  /**
   * Starts the livery autoplay ( disabled for reduced motion, a single livery
   * or a zero interval ).
   *
   * @returns {void}
   */
  _startAutoplay( ){
    if( this._reduced || this._bgButtons.length < 2 || this._interval <= 0 ) return;
    this._scheduleNext( );
  }

  /**
   * Animates the active thumbnail's ring over the interval and advances to the
   * next livery when it completes.
   *
   * @returns {void}
   */
  _scheduleNext( ){
    this._stopTimer( );
    if( this._reduced || this._bgButtons.length < 2 || this._interval <= 0 ) return;

    const ring = this._libreaRings[ this._currentBg ];
    const item = this._bgItems[ this._currentBg ];
    if( ring ){
      this._updateRingGeometry( item , ring );
      this._autoTimer = gsap.fromTo( ring ,
        { strokeDashoffset: 100 } ,
        {
          strokeDashoffset: 0 ,
          duration: this._interval ,
          ease: 'none' ,
          // Keep the stroke tracing the pill while the label reveals / resizes.
          onUpdate: ( ) => this._updateRingGeometry( item , ring ) ,
          onComplete: ( ) => this._advance( )
        }
      );
    } else {
      this._autoTimer = gsap.delayedCall( this._interval , ( ) => this._advance( ) );
    }
  }

  /**
   * Advances to the next livery ( wrapping around ).
   *
   * @returns {void}
   */
  _advance( ){
    this._selectBg( ( this._currentBg + 1 ) % this._bgButtons.length );
  }

  /**
   * Stops the autoplay timer / ring tween.
   *
   * @returns {void}
   */
  _stopTimer( ){
    if( this._autoTimer ){
      this._autoTimer.kill( );
      this._autoTimer = null;
    }
  }

  /* ============
   * Size presets
   * ===========*/
  _bindSizePresets( ){
    this._sizeButtons.forEach( ( button ) => {
      this._bindActivation( button , ( ) => {
        const width = parseInt( button.getAttribute( `data-${PREFIX}-marco-size` ) , 10 );
        if( !this._frame || !width ) return;

        const { min , max } = this._bounds( );
        const target = clamp( width , min , max );
        this._activate( this._sizeButtons , button );
        this._setWidth( target , true );
        this._syncCurve( target );
        this._syncSizeState( target );
        this._hideHelper( );
      } );
    } );
  }

  /* ============
   * Logo switch
   * ===========*/
  _bindLogo( ){
    this._logoButtons.forEach( ( button ) => {
      this._bindActivation( button , ( ) => {
        const index = parseInt( button.getAttribute( `data-${PREFIX}-marco-logo` ) , 10 );
        this._activate( this._logoButtons , button );
        this._crossfade( this._logoLayers , index );
        this._hideHelper( );
      } );
    } );
  }

  /* ============
   * Manual resize ( symmetric drag from center )
   * ===========*/
  _bindHandles( ){
    if( !this._frame ) return;

    this._handles.forEach( ( handle ) => {
      const side = handle.getAttribute( `data-${PREFIX}-marco-handle` ) === 'left' ? -1 : 1;

      // Off-DOM proxy: Draggable tracks the pointer through it without moving
      // any visible element, so the handle keeps its CSS anchoring.
      const proxy = document.createElement( 'div' );
      let startWidth = 0;
      let startPointer = 0;

      const self = this;
      const instance = Draggable.create( proxy , {
        type: 'x' ,
        trigger: handle ,
        onPress( ){
          // Capture the width and pointer at the start of THIS drag. Using the
          // pointer delta ( not the proxy's accumulated `x` ) keeps every drag
          // relative to its own start, so clamping at the min/max width never
          // leaves an offset that has to be "undone" on the next drag.
          startWidth = self._frame.offsetWidth;
          startPointer = this.pointerX;
        } ,
        onDrag( ){
          const { min , max } = self._bounds( );
          // Symmetric: the frame grows/shrinks on both sides at once, so a
          // drag of `delta` px changes the width by `2 * delta`.
          const delta = ( this.pointerX - startPointer ) * side;
          const width = clamp( startWidth + ( delta * 2 ) , min , max );
          self._setWidth( width , false );
          // Highlight the matching preset ( e.g. at the desktop/mobile ends ).
          self._syncSizeToWidth( width );
          // Hide the bottom-right curve below the tablet width.
          self._syncCurve( width );
          // Update the logo composition for the current size state.
          self._syncSizeState( width );
          self._hideHelper( );
        }
      } )[ 0 ];

      this._draggables.push( instance );
    } );
  }

  /* ============
   * Helpers
   * ===========*/

  /**
   * Sets the frame width, optionally tweened. Only the width changes — the
   * height is kept constant, so the handles resize the frame horizontally.
   *
   * @param {number}  width
   * @param {boolean} animate
   * @returns {void}
   */
  _setWidth( width , animate ){
    if( !this._frame ) return;
    if( animate && !this._reduced ){
      gsap.to( this._frame , { width , duration: this._config.resize , ease: 'power2.inOut' } );
    } else {
      gsap.set( this._frame , { width } );
    }
  }

  /**
   * Shows the bottom-right curve only above the tablet size ( i.e. at the
   * desktop frame size ); hides it at the tablet and mobile sizes.
   *
   * @param {number} width
   * @returns {void}
   */
  _syncCurve( width ){
    if( !this._curve || !this._desktopWidth ) return;
    const desktopThreshold = ( this._tabletWidth + this._desktopWidth ) / 2;
    this._curve.classList.toggle( 'is-hidden' , width < desktopThreshold );
  }

  /**
   * Sets the frame size state ( desktop / tablet / mobile ) from the current
   * width, using the midpoints between presets as thresholds. This state drives
   * how each logo variant composes its wordmark and imago ( see _marco.scss ).
   *
   * @param {number} width
   * @returns {void}
   */
  _syncSizeState( width ){
    if( !this._frame || !this._tabletWidth ) return;

    const desktopThreshold = ( this._tabletWidth + this._desktopWidth ) / 2;
    const mobileThreshold  = ( this._mobileWidth + this._tabletWidth ) / 2;

    let state = 'tablet';
    if( width >= desktopThreshold ){
      state = 'desktop';
    } else if( width < mobileThreshold ){
      state = 'mobile';
    }

    this._frame.classList.remove( 'is-size-desktop' , 'is-size-tablet' , 'is-size-mobile' );
    this._frame.classList.add( `is-size-${state}` );
  }

  /**
   * Highlights the size preset whose width matches the given one ( within a
   * small tolerance ), or clears the group when the width is between presets.
   *
   * @param {number} width
   * @returns {void}
   */
  _syncSizeToWidth( width ){
    const tolerance = 2;
    const match = this._sizeButtons.find( ( button ) => {
      const presetWidth = parseInt( button.getAttribute( `data-${PREFIX}-marco-size` ) , 10 );
      return Math.abs( presetWidth - width ) <= tolerance;
    } );

    if( match ){
      this._activate( this._sizeButtons , match );
    } else {
      this._deactivate( this._sizeButtons );
    }
  }

  /**
   * Crossfades a stack of layers, showing only the one at `index`.
   *
   * @param {HTMLElement[]} layers
   * @param {number}        index
   * @returns {void}
   */
  _crossfade( layers , index ){
    layers.forEach( ( layer , i ) => {
      const active = i === index;
      layer.classList.toggle( 'is-active' , active );
      if( this._reduced ){
        gsap.set( layer , { opacity: active ? 1 : 0 } );
      } else {
        gsap.to( layer , { opacity: active ? 1 : 0 , duration: this._config.fade , ease: 'power1.inOut' } );
      }
    } );
  }

  /**
   * Marks one button active ( `is-active` + `aria-pressed` ) within a group.
   *
   * @param {HTMLElement[]} group
   * @param {HTMLElement}   target
   * @returns {void}
   */
  _activate( group , target ){
    group.forEach( ( button ) => {
      const active = button === target;
      button.classList.toggle( 'is-active' , active );
      button.setAttribute( 'aria-pressed' , String( active ) );
    } );
  }

  /**
   * Clears the active state of a whole button group ( used when a manual drag
   * overrides a size preset ).
   *
   * @param {HTMLElement[]} group
   * @returns {void}
   */
  _deactivate( group ){
    group.forEach( ( button ) => {
      button.classList.remove( 'is-active' );
      button.setAttribute( 'aria-pressed' , 'false' );
    } );
  }

  /**
   * Fades the helper hint out, once.
   *
   * @returns {void}
   */
  _hideHelper( ){
    if( this._helper ) this._helper.classList.add( 'is-hidden' );
  }

  /**
   * Kills every Draggable instance created by this module.
   *
   * @returns {void}
   */
  destroy( ){
    this._stopTimer( );
    this._draggables.forEach( ( instance ) => instance && instance.kill( ) );
    this._draggables = [ ];
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_ROOT );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if( el._marcoInstance ) return;
      el._marcoInstance = new Marco( el );
      instances.push( el._marcoInstance );
    } );
    return instances;
  }
}

export default Marco;
